<template>
	<div :class="$style['dashboard-layout']">
		<NotificationStack />
		<AppHeader />
		<AppAside />
		<main
			id="dashboard-main"
			ref="main"
			:class="$style['dashboard-layout__main']"
		>
			<div
				:class="[$style['dashboard-layout__content'], {
					[$style['dashboard-layout__content--cart']]: isCartStatus === 'open'
				}]"
			>
				<Banner
					v-if="banner && banner.active"
					:banner="banner"
				/>
				<slot />
			</div>
		</main>
		<Cart />
		<AcceptRulesModal
			v-if="departmentRules"
			:rules="departmentRules[0]"
			@closed="departmentRules = null"
		/>
		<!-- GLOBAL MODAL COMPONENT -->
		<component
			:is="TargetComponent"
			v-if="modal.show"
			:key="modal.component"
			v-dynamic-events="modal.events"
			v-bind="modal.props"
		/>
		<button
			v-if="showScrollTop"
			:class="$style['scroll-top']"
			:style="{ bottom: scrollTopButtonBottom + 'px', right: scrollTopButtonRight + 'px' }"
			:title="$t('common.buttons.scroll-to-top')"
			:aria-label="$t('common.buttons.scroll-to-top')"
			@click="scrollToTop"
		>
			<FontAwesomeIcon :icon="['far', 'arrow-up']" />
		</button>
	</div>
</template>
<script>
import AcceptRulesModal from '@/components/Modals/AcceptRulesModal'
import FetchDepartmentRules from '@/store/modules/Departments/Schema/Queries/FetchDepartmentRules.gql'
import AppHeader from '@/components/Layout/AppHeader'
import AppAside from '@/components/Layout/AppAside'
import Cart from '@/components/Layout/Cart'
import Banner from './Banner'
import { NotificationStack } from '@/components/Layout/Notifications'
import { mapGetters } from 'vuex'
import { mapActions as bannerActions } from '@/store/modules/App/Banner/helpers'
import { FETCH_ACTIVE_BANNER } from '@/store/modules/App/Banner/action-types'

export default {
	name: 'DashboardLayout',
	components: {
		AppHeader,
		AppAside,
		NotificationStack,
		Cart,
		Banner,
		AcceptRulesModal
	},
	data () {
		return {
			showPopUpInfo: false,
			showScrollTop: false,
			scrollTopButtonBottom: 24,
			scrollTopButtonRight: 28
		}
	},
	computed: {

		...mapGetters({
			isCartStatus: 'App/Cart/status',
			parts: 'App/Cart/parts',
			banner: 'App/Banner/banner',
			modal: 'App/Banner/modal'
		}),


		TargetComponent () {
			if (this.modal.component) {
				return () => import(
					/* webpackInclude: /\/index\.vue$/ */
					`@/components/Layout/${this.modal.component}/index.vue`
				)
			}
			return null
		},

		managerRole () {
			return this.$auth().managerRole || null
		}
	},
	watch: {
		isCartStatus () {
			this.$nextTick(() => this.updateScrollTopButton())
		}
	},
	created () {
		if (!this.banner) {
			this.fetchBanner()
		}
	},
	mounted () {
		document.documentElement.classList.add('dashboard-scroll-lock')
		this.$nextTick(() => {
			const main = this.$refs.main
			if (main) {
				this._onMainScrollBound = this.onMainScroll.bind(this)
				main.addEventListener('scroll', this._onMainScrollBound, { passive: true })
			}
			this._onWindowResizeBound = this.onMainScroll.bind(this)
			window.addEventListener('resize', this._onWindowResizeBound)
			if (typeof MutationObserver !== 'undefined') {
				this._bottomBarObserver = new MutationObserver(() => this.onMainScroll())
				this._bottomBarObserver.observe(this.$el, { childList: true })
			}
			this.updateScrollTopButton()
		})
	},
	beforeDestroy () {
		document.documentElement.classList.remove('dashboard-scroll-lock')
		const main = this.$refs.main
		if (main && this._onMainScrollBound) {
			main.removeEventListener('scroll', this._onMainScrollBound)
			this._onMainScrollBound = null
		}
		if (this._onWindowResizeBound) {
			window.removeEventListener('resize', this._onWindowResizeBound)
			this._onWindowResizeBound = null
		}
		if (this._bottomBarObserver) {
			this._bottomBarObserver.disconnect()
			this._bottomBarObserver = null
		}
		if (this._scrollTopRaf) {
			cancelAnimationFrame(this._scrollTopRaf)
			this._scrollTopRaf = null
		}
	},
	methods: {
		...bannerActions({
			fetchBanner: FETCH_ACTIVE_BANNER
		}),

		onMainScroll () {
			if (this._scrollTopRaf) return
			this._scrollTopRaf = requestAnimationFrame(() => {
				this._scrollTopRaf = null
				this.updateScrollTopButton()
			})
		},

		// Horizontally the button follows the notification bell's axis in the
		// right whitespace strip. Vertically it floats just above a table bottom
		// bar only while that bar is stuck to the viewport's bottom edge - a
		// footer resting mid-viewport must not hoist the button up; the cart
		// bar/mini cart lift it above.
		updateScrollTopButton () {
			const main = this.$refs.main
			if (!main) return
			const show = main.scrollTop > 400
			if (this.showScrollTop !== show) {
				this.showScrollTop = show
			}
			const buttonSize = 44
			const mainBottom = main.getBoundingClientRect().bottom
			let bottom = 24
			let right = 28
			const bell = document.querySelector('[data-notification-bell]')
			const bellRect = bell && bell.getBoundingClientRect()
			if (bellRect && bellRect.width > 0) {
				right = Math.max(8, Math.round(window.innerWidth - (bellRect.left + bellRect.width / 2) - buttonSize / 2))
			}
			let footerRect = null
			main.querySelectorAll('[data-app-table-sticky-footer]').forEach((el) => {
				const rect = el.getBoundingClientRect()
				if (rect.height <= 0 || Math.abs(rect.bottom - mainBottom) > 2) return
				if (!footerRect || rect.bottom > footerRect.bottom) {
					footerRect = rect
				}
			})
			if (footerRect) {
				bottom = Math.max(8, Math.round(mainBottom - footerRect.top + 16))
			}
			const cart = document.getElementById('cartWrapper')
			const cartRect = cart && cart.getBoundingClientRect()
			if (cartRect && cartRect.height > 0 && Math.abs(cartRect.bottom - mainBottom) <= 2) {
				bottom = Math.max(bottom, Math.round(cartRect.height + 24))
			}
			const cartMini = document.querySelector('[data-cart-mini]')
			const miniRect = cartMini && cartMini.getBoundingClientRect()
			if (miniRect && miniRect.height > 0 && miniRect.bottom <= mainBottom + 2) {
				bottom = Math.max(bottom, Math.round(mainBottom - miniRect.top + 24))
				right = Math.max(8, Math.round(window.innerWidth - (miniRect.left + miniRect.width / 2) - buttonSize / 2))
			}
			if (this.scrollTopButtonBottom !== bottom) {
				this.scrollTopButtonBottom = bottom
			}
			if (this.scrollTopButtonRight !== right) {
				this.scrollTopButtonRight = right
			}
		},

		scrollToTop () {
			const main = this.$refs.main
			if (main) {
				main.scrollTo({ top: 0, behavior: 'smooth' })
			}
		}
	},
	apollo: {
		departmentRules: {
			query: FetchDepartmentRules,
			fetchPolicy: 'network-only',
			variables () {
				return {
					payload: {
						departmentId: this.managerRole.departmentId,
						unresolved: true
					},
					page: 1,
					size: 20
				}
			},
			update ({ rules }) {
				if (rules && rules.nodes && rules.nodes.length) {
					return rules.nodes
				}
				return null
			},
			skip () {
				const rules = [
					!this.$auth().admin,
					!!this.managerRole
				]
				return !rules.every(rule  => rule)
			}
		}
	}
}
</script>
<style lang="scss" module>



	.dashboard-layout {
		--app-header-height: #{map-deep-get($header, height, lg)};

		position: fixed;
		inset: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background-color: getColor(bg);

		@include media-breakpoint-down(md) {
			--app-header-height: #{map-deep-get($header, height, sm)};
		}

		&__main {
			flex: 1;
			min-width: 0;
			min-height: 0;
			overflow: hidden auto;
			overscroll-behavior-y: contain;
			scroll-behavior: smooth;
		}

		&__content {
			position: relative;
			padding: $gutter $gutter * 2;
			padding-left: map-deep-get($header, height, lg);

			@include media-breakpoint-down(md) {
				padding: $gutter;
				padding-bottom: $gutter * 9;
			}

			&--cart {
				padding-bottom: 90px;
			}
		}

	.scroll-top {
		position: fixed;
		z-index: 80;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		border: 1px solid getColor(border);
		border-radius: 50%;
		background-color: getColor(surface-elevated);
		color: getColor(helper-text);
		box-shadow: 0 4px 16px getColor(shadow);
		cursor: pointer;
		transition: bottom 0.2s ease, right 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

		@include dark-mode-border-fix(border);

		&:hover {
			color: getColor(primary);
			background-color: getColor(hover-overlay);
			box-shadow: 0 6px 20px getColor(shadow-dark);
			transform: translateY(-2px);
		}
	}

	}
</style>
